import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  PhoneAuthProvider as FBPhoneAuthProvider,
  updatePhoneNumber,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  PhoneAuthProviderFactory,
  PhoneAuthProvider,
  PhoneAuthSession,
  PhoneAuthError,
} from '../../services/phoneAuth';

const maskPhoneNumber = (phone: string): string => {
  if (!phone || phone.length < 4) return phone;
  const last4 = phone.slice(-4);
  const prefix = phone.startsWith('+') ? phone.slice(0, 3) : phone.slice(0, 2);
  return `${prefix} **** ${last4}`;
};

const SMSTwoFactorSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<'intro' | 'phone' | 'verify' | 'complete'>(
    'intro'
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingPhoneNumber, setExistingPhoneNumber] = useState<string | null>(
    null
  );

  // Provider abstraction state
  const [authProvider, setAuthProvider] = useState<PhoneAuthProvider | null>(
    null
  );
  const [authSession, setAuthSession] = useState<PhoneAuthSession | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  // Load existing phone number from Firestore on mount
  useEffect(() => {
    const loadExisting = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();
        if (data?.phoneNumber) {
          setExistingPhoneNumber(data.phoneNumber);
        }
      } catch (err) {
        console.error('[SMSSetup] Failed to load existing phone number:', err);
      }
    };
    loadExisting();
  }, [user]);

  // Initialize provider when entering 'phone' step
  useEffect(() => {
    if (step !== 'phone') return;

    const initProvider = async () => {
      // Wait for DOM to render the reCAPTCHA container
      await new Promise((resolve) => setTimeout(resolve, 300));

      const container = document.getElementById('recaptcha-container');
      if (!container) {
        console.error('[SMSSetup] reCAPTCHA container not found');
        setError('Page setup error. Please reload.');
        return;
      }

      try {
        const provider = PhoneAuthProviderFactory.createProvider();
        await provider.initialize();
        setAuthProvider(provider);
        console.log('[SMSSetup] Provider initialized');
      } catch (err) {
        console.error('[SMSSetup] Provider init error:', err);
        setError('Failed to initialize verification. Please try again.');
      }
    };

    initProvider();
  }, [step]);

  // Cleanup provider on unmount
  useEffect(() => {
    return () => {
      authProvider?.dispose();
    };
  }, [authProvider]);

  const handleSendCode = async () => {
    setError('');
    setLoading(true);

    if (!authProvider) {
      setError('Verification system not ready. Please wait or reload.');
      setLoading(false);
      return;
    }

    try {
      const formattedPhone = phoneNumber.startsWith('+')
        ? phoneNumber
        : `+${phoneNumber}`;

      // sendVerificationCode calls signInWithPhoneNumber internally —
      // this only sends the SMS, it does NOT change the current auth session
      const session = await authProvider.sendVerificationCode(formattedPhone);
      setAuthSession(session);
      setStep('verify');
    } catch (err: any) {
      console.error('[SMSSetup] Send error:', err);
      if (err instanceof PhoneAuthError) {
        setError(err.message);
      } else {
        setError(err.message || 'Failed to send verification code.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    setLoading(true);

    if (!authSession?.confirmationResult) {
      setError('Verification session not found. Please go back and resend.');
      setLoading(false);
      return;
    }

    try {
      // Check if phone number is already registered to another user (client-side validation for UX)
      const formattedPhone = phoneNumber.startsWith('+')
        ? phoneNumber
        : `+${phoneNumber}`;

      const checkAvailability = httpsCallable(
        functions,
        'checkPhoneNumberAvailability'
      );
      const availabilityResult = await checkAvailability({
        phoneNumber: formattedPhone,
      });
      const { available } = availabilityResult.data as {
        available: boolean;
        registeredToSelf: boolean;
      };

      if (!available) {
        setError(
          'This phone number is already registered to another account. Please use a different number.'
        );
        setLoading(false);
        return;
      }

      // Create credential from verificationId + code.
      // This does NOT sign in or change auth state — it's just a credential object.
      const credential = FBPhoneAuthProvider.credential(
        authSession.confirmationResult.verificationId,
        verificationCode
      );

      // Link / update phone on the current (email/password) user.
      // The email session stays alive — no sign-out occurs.
      await updatePhoneNumber(auth.currentUser!, credential);
      console.log('[SMSSetup] Phone number updated on Firebase Auth');

      // Persist smsFactorEnabled + phoneNumber to Firestore via Cloud Function
      const logVerification = httpsCallable(functions, 'logPhoneVerification');
      await logVerification({ phoneNumber: formattedPhone });
      console.log('[SMSSetup] logPhoneVerification called');

      setStep('complete');
    } catch (err: any) {
      console.error('[SMSSetup] Verify error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid verification code. Please try again.');
      } else if (err.code === 'auth/code-expired') {
        setError(
          'Verification code expired. Please go back and request a new one.'
        );
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('This phone number is already linked to another account.');
      } else if (err instanceof PhoneAuthError) {
        setError(err.message);
      } else {
        setError(err.message || 'Verification failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Info banner — shown on every step to reinforce which number the mobile app will use
  const InfoBanner = () => (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
      <p className="text-sm text-blue-800">
        💡 This phone number is what your mobile app uses for SMS login
        verification. Make sure this device can receive SMS on this number.
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Invisible reCAPTCHA container — must be in DOM before provider initialises */}
        <div
          id="recaptcha-container"
          style={{
            width: 0,
            height: 0,
            overflow: 'hidden',
            position: 'absolute',
          }}
        ></div>

        {/* Intro Step */}
        {step === 'intro' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                <svg
                  className="w-10 h-10 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">
                {existingPhoneNumber
                  ? 'Update SMS 2FA Phone Number'
                  : 'Enable SMS Two-Factor Authentication'}
              </h1>
              <p className="text-gray-600 text-lg">
                {existingPhoneNumber
                  ? 'Change the phone number used for SMS verification'
                  : 'Add an extra layer of security with SMS codes'}
              </p>
            </div>

            <InfoBanner />

            {/* Warn before changing an existing number */}
            {existingPhoneNumber && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  ⚠️ Current phone number
                </p>
                <p className="text-sm text-amber-700 font-mono">
                  {maskPhoneNumber(existingPhoneNumber)}
                </p>
                <p className="text-sm text-amber-700 mt-2">
                  Changing this will affect your mobile app login. The new
                  number must be able to receive SMS.
                </p>
              </div>
            )}

            <div className="space-y-4 mb-8">
              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">📱</span>
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold text-lg mb-2">
                      SMS Verification
                    </h3>
                    <p className="text-gray-600">
                      Receive one-time codes via text message to verify your
                      identity.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">📞</span>
                  </div>
                  <div>
                    <h3 className="text-gray-900 font-semibold text-lg mb-2">
                      Phone Number Required
                    </h3>
                    <p className="text-gray-600">
                      You'll need a mobile phone number that can receive SMS
                      messages.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={() => setStep('phone')}
              disabled={loading}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {existingPhoneNumber ? 'Update Phone Number' : 'Continue'}
            </button>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-4 text-gray-600 hover:text-gray-900 py-3 rounded-lg font-medium transition-colors"
            >
              Maybe Later
            </button>
          </div>
        )}

        {/* Phone Entry Step */}
        {step === 'phone' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Enter Phone Number
              </h2>
              <p className="text-gray-600">
                We'll send a verification code to this number
              </p>
            </div>

            <InfoBanner />

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-center text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-gray-700 text-sm font-medium mb-3">
                Phone Number
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+1234567890"
                disabled={loading}
                className="w-full px-6 py-4 text-center text-xl bg-white border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors disabled:bg-gray-100"
              />
              <p className="text-gray-500 text-xs text-center mt-2">
                Include country code (e.g., +1 for US, +65 for Singapore)
              </p>
            </div>

            <button
              onClick={handleSendCode}
              disabled={loading || phoneNumber.length < 8}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending Code...' : 'Send Verification Code'}
            </button>

            <button
              onClick={() => {
                setStep('intro');
                setError('');
              }}
              className="w-full mt-4 text-gray-600 hover:text-gray-900 py-3 rounded-lg font-medium transition-colors"
            >
              ← Back
            </button>
          </div>
        )}

        {/* Verify Code Step */}
        {step === 'verify' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Enter Verification Code
              </h2>
              <p className="text-gray-600">
                Check your phone for the 6-digit code
              </p>
              <p className="text-gray-500 text-sm mt-2">
                Sent to {phoneNumber}
              </p>
            </div>

            <InfoBanner />

            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-center text-sm">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) =>
                  setVerificationCode(
                    e.target.value.replace(/\D/g, '').slice(0, 6)
                  )
                }
                placeholder="000000"
                maxLength={6}
                disabled={loading}
                autoFocus
                className="w-full px-6 py-4 text-center text-2xl font-mono tracking-widest bg-white border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:outline-none transition-colors disabled:bg-gray-100"
              />
            </div>

            <button
              onClick={handleVerifyCode}
              disabled={loading || verificationCode.length !== 6}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'Verify and Enable SMS 2FA'}
            </button>

            <button
              onClick={() => {
                setStep('phone');
                setVerificationCode('');
                setError('');
              }}
              className="w-full mt-4 text-gray-600 hover:text-gray-900 py-3 rounded-lg font-medium transition-colors"
            >
              ← Resend Code
            </button>
          </div>
        )}

        {/* Complete Step */}
        {step === 'complete' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-4">
                <svg
                  className="w-10 h-10 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                SMS 2FA Enabled!
              </h2>
              <p className="text-gray-600">
                Your phone number has been verified and updated successfully.
              </p>
              <p className="text-gray-500 text-sm mt-3">
                Your mobile app will now use{' '}
                <span className="font-semibold text-gray-700">
                  {maskPhoneNumber(
                    phoneNumber.startsWith('+')
                      ? phoneNumber
                      : `+${phoneNumber}`
                  )}
                </span>{' '}
                for SMS login verification.
              </p>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors"
            >
              Continue to Dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SMSTwoFactorSetupPage;
